/**
 * What the chats list and the open chat's header share for one storage scope
 * (`<crm user>::<account>`): the person's own fan names, kept in the CRM's
 * browser-storage keys (so the old page agrees), what this session changed on
 * OnlyFans before the list reloads (mute, hidden chats), and the Pinned chats
 * on this Account (the person's server board, ADR 0035).
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { memoryPins, type AccountPins, type FanSnapshot } from './accountPins'
import { useChatsDeps, type ChatsDeps } from './ChatsDeps'
import { readCustomNames } from './storage'

export const FAN_NAME_MAX = 100

interface Prefs {
  names: Record<string, string>
  /** Mute state set here, over what OnlyFans' chat row says. */
  muted: Map<string, boolean>
  /** Chats OnlyFans' rows on the list say are muted. */
  listMuted: Set<string>
  /** Chats hidden or cleared here: off the list until it reloads. */
  gone: Set<string>
}

const stores = new Map<string, { prefs: Prefs; listeners: Set<() => void> }>()

function storeFor(scope: string) {
  let store = stores.get(scope)
  if (!store) {
    store = { prefs: { names: readCustomNames(scope), muted: new Map(), listMuted: new Set(), gone: new Set() }, listeners: new Set() }
    stores.set(scope, store)
  }
  return store
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* storage unavailable */
  }
}

function update(scope: string, change: (p: Prefs) => Partial<Prefs>) {
  const store = storeFor(scope)
  store.prefs = { ...store.prefs, ...change(store.prefs) }
  store.listeners.forEach((fn) => fn())
}

/** Pages given no pin source keep pins in memory, one set per deps object. */
const memory = new WeakMap<ChatsDeps, AccountPins>()
function pinsOf(deps: ChatsDeps): AccountPins {
  if (deps.pins) return deps.pins
  let pins = memory.get(deps)
  if (!pins) memory.set(deps, (pins = memoryPins()))
  return pins
}

export function useChatPrefs() {
  const deps = useChatsDeps()
  const { storageScope: scope } = deps
  const pins = pinsOf(deps)
  const pinned = useSyncExternalStore(pins.subscribe, pins.get)
  const prefs = useSyncExternalStore(
    useCallback(
      (fn: () => void) => {
        const { listeners } = storeFor(scope)
        listeners.add(fn)
        return () => {
          listeners.delete(fn)
          // Nobody on the page: the next one reads browser storage afresh.
          if (!listeners.size) stores.delete(scope)
        }
      },
      [scope],
    ),
    () => storeFor(scope).prefs,
  )
  const actions = useMemo(
    () => ({
      /** Pins or unpins at once; the server's answer says if it held (`full`: the 100-pin board). */
      togglePin(fanId: string, fan?: FanSnapshot) {
        return pins.toggle(fanId, fan)
      },
      /** The person's own name for a fan; empty clears it (fan-rename.js setFanCustomNameLocal). */
      setName(fanId: string, name: string) {
        update(scope, (p) => {
          const names = { ...p.names }
          const trimmed = name.trim().slice(0, FAN_NAME_MAX)
          if (trimmed) names[fanId] = trimmed
          else delete names[fanId]
          write(`crm_fan_custom_names_${scope}`, names)
          return { names }
        })
      },
      /** The list reports which of its chats OnlyFans has muted. */
      seeMuted(fanIds: string[]) {
        const now = storeFor(scope).prefs.listMuted
        if (fanIds.length === now.size && fanIds.every((id) => now.has(id))) return
        update(scope, () => ({ listMuted: new Set(fanIds) }))
      },
      setMuted(fanId: string, muted: boolean) {
        update(scope, (p) => ({ muted: new Map(p.muted).set(fanId, muted) }))
      },
      drop(fanId: string) {
        update(scope, (p) => ({ gone: new Set(p.gone).add(fanId) }))
      },
    }),
    [scope, pins],
  )
  return { ...prefs, pinned, ...actions }
}
